<template>
  <div style="padding-bottom: 200px;">
    <main-toolbar>
      <v-toolbar-title>Dashboard</v-toolbar-title>
      <template slot="extension">
        <v-flex class="pr-1">
          <DateRangePicker dialog light v-model="dateRange" @input="update" />
        </v-flex>
        <v-flex class="pl-1">
          <MachinesPicker light :noTopPadding="!appSmall" v-model="selectedMachines" multiple />
        </v-flex>
        <v-flex shrink="100" class="pl-2">
          <v-btn
            icon
            large
            @click="update"
            text
            light
            :disabled="loading || loadingOverallTotals || machinesLoading"
          >
            <v-icon>fa-check</v-icon>
          </v-btn>
        </v-flex>
      </template>
    </main-toolbar>
    <div v-if="!initialLoad && !loading" class="pa-3" style="min-height: 60vh; align-items: center; justify-content: center; display: flex;">
      Please select a date range and machine list to view data.
    </div>
    <v-layout row wrap class="pt-3 ml-5 mr-5" v-if="initialLoad">
      <v-flex xs6 md3 style="font-size: 30px;display: flex;justify-content: center;align-items:center;">
        <Gauge
          :id="`dashboard-overall`"
          :value="(overallTotals.overall || 0)"
          label="Overall"
          :forceGray="overallTotals.overall === undefined || !overallTotals.total_pallets"
          :loading="loadingOverallTotals"
        />
      </v-flex>
      <v-flex
        xs6
        md3
        style="font-size: 30px;display: flex;justify-content: center;align-items:center;"
        v-for="indicator in performanceIndicators"
        :key="indicator.key"
      >
        <Gauge
          :id="`dashboard-${indicator.key}`"
          :value="performanceValues[indicator.key]"
          :forceGray="performanceValues[indicator.key] === undefined || !overallTotals.total_pallets"
          :label="indicator.name"
          :loading="loadingPerformance[indicator.key] || loadingOverallTotals"
         />
      </v-flex>
    </v-layout>
    <v-layout row wrap justify-center class="mt-3 ml-5 mr-5" v-if="initialLoad">
      <v-flex xs12 md3 xl2>
        <Indicator
          label="Loads"
          :value="round(overallTotals.total_pallets || 0).toLocaleString()"
          :loading="loadingOverallTotals"
        />
      </v-flex>
      <v-flex xs12 md3 xl2>
        <Indicator
          label="Flagged Loads"
          :value="round(overallTotals.flagged_loads || 0).toLocaleString()"
          :loading="loadingOverallTotals"
        />
      </v-flex>
      <v-flex xs12 md3 xl2>
        <Indicator
          label="Film Weight"
          shrinkIfLong
          :value="round(overallTotals.total_film_weight || '0').toLocaleString() + ' ' + unitName('oz')"
          :loading="loadingOverallTotals"
        />
      </v-flex>
      <v-flex xs12 md3 xl2>
        <Indicator
          label="Average Stretch"
          :value="round(overallTotals.total_avg_stretch_pct || '0') + '%'"
          :loading="loadingOverallTotals"
        />
      </v-flex>
    </v-layout>
    <v-layout class="mt-5" v-if="!loadingOverallTotals && initialLoad">
      <v-flex xs12 md10 offset-md1>
        <v-expansion-panels>
          <v-expansion-panel
            lazy
            v-for="machine in machineList"
            :key="machine.id"
          >
            <v-expansion-panel-header>
              <div class="machine-header" style="width: 95%;">
                <div class="mh-column" style="width: 3em;">
                  <div class="machine-dot" :style="{backgroundColor: machine.color}"></div>
                </div>
                <div class="mh-column" :style="{width: appSmall ? 'calc(95% - 6em)' : 'calc(100% - 14em)'}">
                  <div class="machine-name mr-2">{{machine.id}} - {{machine.name}}</div>
                </div>
                <div class="mh-column" :style="{width: appSmall ? '3em' : '11em'}">
                  <div class="machine-percent">{{appSmall ? '' : 'Overall '}}{{round(machine.overall || 0)}}%</div>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <MachineDetails :date-range="dateRange" :machine="machine" @openGraph="graphParams = $event" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-flex>
    </v-layout>
    <GraphChartDialog
      v-if="graphParams"
      :initialParams="graphParams"
      @close="graphParams = null"
    />
  </div>
</template>

<style lang="scss">
.machine-header {
  vertical-align: middle;
  .mh-column {
    display: inline-block;
    height: 100%;
    vertical-align: middle;
  }
  .machine-dot {
    width: 2em;
    height: 2em;
    border-radius: 1em;
    background-color: #efefef;
    display: inline-block;
    vertical-align: middle;
  }
  .machine-name {
    font-size: 1.3em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .machine-percent {
    font-size: 1.4em;
    font-weight: bold;
  }
}
</style>

<script>
import Gauge from './Gauge'
import Indicator from './Indicator'
import MachineDetails from './MachineDetails'
import { mapState, mapActions, mapMutations } from 'vuex'
import DateRangePicker from '@/components/DateRangePicker'
import MachinesPicker from '@/components/MachinesPicker'
import axios from 'axios'
import moment from 'moment-timezone'
import get from 'lodash/get'
import round from 'lodash/round'
import getColor from './getColor'
import intersection from 'lodash/intersection'
import GraphChartDialog from '@/modules/Graphs/GraphChartDialog'

export default {
  components: {
    Gauge,
    Indicator,
    MachineDetails,
    DateRangePicker,
    MachinesPicker,
    GraphChartDialog
  },
  data () {
    return {
      loading: true,
      loadingOverallTotals: true,
      initialLoad: false, // Indicates that everything should show up at all
      overallTotals: {},
      loadingPerformance: {},
      performanceValues: {},
      dateRange: {
        start: null,
        end: null
      },
      selectedMachines: [],
      performanceIndicators: [
        { name: 'Revs', key: 'revconformity' },
        { name: 'Stretch', key: 'asconformity' },
        { name: 'Unwind', key: 'unwconformity' }
      ],
      requestCount: 0,
      graphParams: null
    }
  },
  computed: {
    ...mapState('userState', {
      userState: 'dashboard'
    }),
    ...mapState('machines', {
      machinesLoading: 'loading',
      machines: 'data'
    }),
    machineList () {
      return this.selectedMachines.map(machineId => {
        let overallTotal = get(this.overallTotals, `machines.${machineId}`)
        if (overallTotal) {
          return {
            ...overallTotal,
            id: overallTotal.machine,
            name: overallTotal.name,
            color: getColor(round(overallTotal.overall), !overallTotal.total_pallets)
          }
        }
        let machine = this.machines.find(m => m.id + '' === machineId + '')
        return {
          noTotal: true, // API returns an internal server error when attempting to get details like this for a missing machine
          overall: 0,
          total_pallets: 0,
          id: machineId,
          name: machine ? machine.name : '',
          color: getColor(round(machine.overall), true)
        }
      })
    },
    requestParams () {
      const start = moment(this.dateRange.start)
      const end = moment(this.dateRange.end)
      const aYearAgo = moment().subtract(1, 'year')

      return {
        ...(this.selectedMachines.length ? { machines: this.selectedMachines.join(',') } : {}),
        fromDate: start.format('YYYY-MM-DD[T]HH:mm:ss'),
        toDate: end.format('YYYY-MM-DD[T]HH:mm:ss'),
        ...(start.isBefore(aYearAgo) || end.isBefore(aYearAgo) ? { archive: 1 } : {})
      }
    },
    hasAllParameters () {
      return this.dateRange && this.dateRange.start && this.dateRange.end && this.selectedMachines && this.selectedMachines.length
    }
  },
  methods: {
    round,
    ...mapMutations('userState', {
      setState: 'setDashboard'
    }),
    ...mapActions('machines', {
      loadMachines: 'find'
    }),
    async loadPerformanceIndicators () {
      // Date range is mandatory
      if (!this.hasAllParameters) {
        return
      }
      let requestCount = this.requestCount
      // Load all performance endpoints
      this.performanceIndicators.forEach(indicator => (this.$set(this.loadingPerformance, indicator.key, true)))
      let performancePromise = Promise.all(this.performanceIndicators.map(async (indicator) => {
        try {
          let response = await axios({
            url: `/rest/dashboard/${indicator.key}`,
            method: 'GET',
            params: this.requestParams
          })
          if (this.requestCount !== requestCount) {
            return
          }
          this.$set(this.performanceValues, indicator.key, 100 * (get(response, 'data.score') || 0))
          this.$set(this.loadingPerformance, indicator.key, false)
        } catch (e) {
          this.$set(this.loadingPerformance, indicator.key, false)
          throw e
        }
      }))

      await performancePromise
    },
    async loadOverallTotals () {
      if (!this.hasAllParameters) {
        return
      }
      let requestCount = this.requestCount
      this.loadingOverallTotals = true
      try {
        let response = await axios({
          method: 'GET',
          url: '/rest/dashboard/totals',
          params: this.requestParams
        })
        if (this.requestCount !== requestCount) {
          return
        }
        this.overallTotals = get(response, 'data') || {}
        this.loadingOverallTotals = false
      } catch (e) {
        this.loadingOverallTotals = false
        throw e
      }
    },
    async update () {
      this.initialLoad = true
      this.setState({
        dateRange: this.dateRange,
        machines: this.selectedMachines
      })
      this.requestCount++
      await Promise.all([
        this.loadPerformanceIndicators(),
        this.loadOverallTotals()
      ])
    }
  },
  async created () {
    if (this.userState.dateRange) {
      this.dateRange = this.userState.dateRange
    }
    this.dateRange = this.dateRange || {}
    this.dateRange.start = this.$route.query.fromDate || this.dateRange.start
    this.dateRange.end = this.$route.query.toDate || this.dateRange.end

    if (this.userState.machines) {
      this.selectedMachines = this.userState.machines
    }
    if (this.$route.query.machines) {
      this.selectedMachines = this.$route.query.machines.split(',')
    }
    try {
      await this.loadMachines()
      // Make sure there are no deleted machines that have been saved, or machines the user doesn't have access to
      this.selectedMachines = intersection(this.machines.map(m => m.id) || [], this.selectedMachines || [])
      // Select all by default if none are selected
      if (!(this.selectedMachines && this.selectedMachines.length)) {
        this.selectedMachines = this.machines.map(m => m.id)
      }
      if (!this.hasAllParameters) {
        this.loading = this.loadingOverallTotals = false
        return
      }
      await this.update()
      this.loading = false
    } catch (e) {
      this.loading = false
      throw e
    }
  }
}
</script>
